import React, { useEffect } from 'react';
import { Route } from 'react-router-dom';
import { AnimatedSwitch } from 'react-router-transition';
import { initOptimize } from './analytics/GoogleAnalytics';
import { Onboarding } from './onboarding/Onboarding';
import { QuickPurchase } from './quickpurchase/QuickPurchase';
import SignIn from './signin/SignIn';
import { PresalePage } from './subscription/presale/PresalePage';
import { Subscription } from './subscription/Subscription';
import { Congrats } from './success/congrats/Congrats';
import { InvitePurchase } from './invitepurchase/InvitePurchase';
import { GiftCards } from './giftcards/GiftCards';
import { Redeem } from 'redeem/Redeem';
import { Amplitude } from './analytics/Amplitude';
import RouterPrompt from './components/routerprompt/RouterPrompt';
import Install from './install/Install';
import Welcome from 'welcome/Welcome';
import { Preplan } from 'subscription/prepaln/Preplan';
import { Workbook } from 'subscription/workbook/Workbook';
import { SubscriptionCancel } from 'cancelsubscription/SubscriptionCancel';
import { GrowthBookProvider } from '@growthbook/growthbook-react';
import {
  getOrCreateAnonymousId,
  growthbook,
} from 'onboarding/helpers/growthbook';
import { BasicUpsellFlow } from 'subscription/upsell/basicUpsellFlow/UpsellFlow';
import { MonthWeekUpsellFlow } from 'subscription/upsell/monthWeekUpsellFlow/UpsellFlow';
import { QuarterUpsell } from 'subscription/upsell/quarterUpsell/QuarterUpsell';
import LivenSignIn from 'bootstrap/livensignin/SignIn';
import { LivenSubscription } from 'livensubscription/LivenSubscription';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { getQueryVariable } from 'utils';
import './App.scss';

const App = () => {
  const initAmplitudeUserProperties = (): void => {
    const width = window.screen.width * window.devicePixelRatio;
    const height = window.screen.height * window.devicePixelRatio;

    Amplitude.setUserProperty('user_agent', window.navigator.userAgent);
    Amplitude.setUserProperty('screen_resolution', `${width}x${height}`);
  };

  const setupGrowthBook = async () => {
    if (!growthbook) return;

    growthbook.setAttributes({
      id: getOrCreateAnonymousId(),
      url: window.location.href,
      userAgent: navigator.userAgent,
      flow: getQueryVariable('flow'),
    });

    growthbook.init({ streaming: true });
  };

  useEffect(() => {
    initOptimize();
    initAmplitudeUserProperties();
    setupGrowthBook();
  }, []);

  return (
    <div className="App">
      <GoogleReCaptchaProvider
        reCaptchaKey={process.env.REACT_APP_RECAPTCHA_KEY || ''}
      >
        <GrowthBookProvider growthbook={growthbook}>
          <div className="AppContainer">
            <AnimatedSwitch
              atEnter={{ opacity: 0 }}
              atLeave={{ opacity: 1 }}
              atActive={{ opacity: 1 }}
              className="switch-wrapper"
            >
              <Route exact path="/create-account" component={SignIn} />
              <Route
                exact
                path="/wellness-create-account"
                component={LivenSignIn}
              />
              <Route exact path="/plan" component={PresalePage} />
              <Route exact path="/preplan" component={Preplan} />
              <Route exact path="/subscription" component={Subscription} />
              <Route
                exact
                path="/wellness-subscription"
                component={LivenSubscription}
              />
              <Route exact path="/workbook" component={Workbook} />
              <Route path="/basic-upsell" component={BasicUpsellFlow} />
              <Route path="/weekmonth-upsell" component={MonthWeekUpsellFlow} />
              <Route path="/quarter-upsell" component={QuarterUpsell} />
              <Route exact path="/done" component={Congrats} />
              <Route
                exact
                path="/onboarding/:questionId"
                component={Onboarding}
              />
              <Route exact path="/onboarding" component={Onboarding} />
              <Route path="/quick-purchase" component={QuickPurchase} />
              <Route path="/invite" component={InvitePurchase} />
              <Route path="/install" component={Install} />
              <Route path="/gift" component={GiftCards} />
              <Route path="/redeem" component={Redeem} />
              <Route
                path="/subscription-cancel/:questionId"
                component={SubscriptionCancel}
              />
              <Route component={Welcome} />
            </AnimatedSwitch>
            <RouterPrompt />
          </div>
        </GrowthBookProvider>
      </GoogleReCaptchaProvider>
    </div>
  );
};

export default App;
